import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { DevicePlatform } from '@services/backend/models/generated-models';

@Injectable({ providedIn: 'root' })
export class PlatformService {

    // region is(platform)

    private _isAndroid: boolean = undefined;
    get isAndroid(): boolean {
        if (this._isAndroid === undefined) {
            this._isAndroid = this.platform.is('android');
        }
        return this._isAndroid;
    }

    private _isDesktop: boolean = undefined;
    get isDesktop(): boolean {
        if (this._isDesktop === undefined) {
            // Check Ionic's desktop detection first
            this._isDesktop = this.platform.is('desktop');
            
            // If not detected as desktop, use direct user agent and window checks
            if (!this._isDesktop) {
                try {
                    // Get user agent directly from window (with fallback)
                    const userAgent = (typeof window !== 'undefined' && window.navigator && window.navigator.userAgent) 
                        ? window.navigator.userAgent.toLowerCase() 
                        : '';

                    console.log(`((==)) User agent: ${userAgent}`);
                    
                    // Check for desktop operating systems in user agent
                    const hasDesktopOS = /windows|win32|win64|macintosh|mac os|linux|ubuntu|fedora|debian|x11/.test(userAgent);
                    
                    // Check for actual mobile device indicators (be specific to avoid false positives)
                    const hasMobileDevice = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini|windows phone|windows mobile/.test(userAgent);
                    
                    // Check for tablet indicators
                    const hasTabletDevice = /ipad|android.*tablet|tablet/.test(userAgent);
                    
                    // Get window width (with fallback)
                    let width = 0;
                    try {
                        width = this.platform.width();
                    } catch (e) {
                        width = typeof window !== 'undefined' ? window.innerWidth : 0;
                    }
                    const isWideScreen = width >= 768; // Desktop typically has width >= 768px
                    
                    // Primary check: Desktop OS without mobile/tablet device = desktop
                    // This is the most reliable indicator for Windows/Mac/Linux desktops
                    if (hasDesktopOS && !hasMobileDevice && !hasTabletDevice) {
                        this._isDesktop = true;
                    } 
                    // Secondary check: Wide screen without mobile device indicators
                    else if (!hasMobileDevice && !hasTabletDevice && isWideScreen) {
                        this._isDesktop = true;
                    }
                    
                    // Debug logging for desktop detection troubleshooting
                    console.log(`((==)) Desktop detection - Ionic desktop: ${this.platform.is('desktop')}, hasDesktopOS: ${hasDesktopOS}, hasMobileDevice: ${hasMobileDevice}, hasTabletDevice: ${hasTabletDevice}, width: ${width}, isWideScreen: ${isWideScreen}, final isDesktop: ${this._isDesktop}`);
                } catch (e) {
                    console.error('((==)) Error in desktop detection:', e);
                }
            }
        }
        return this._isDesktop;
    }

    private _isIos: boolean = undefined;
    get isIos(): boolean {
        if (this._isIos === undefined) {
            this._isIos = this.platform.is('ios') || this.platform.is('ipad') || this.platform.is('iphone');
        }
        return this._isIos;
    }

    private _isIpad: boolean = undefined;
    get isIpad(): boolean {
        if (this._isIpad === undefined) {
            this._isIpad = this.platform.is('ipad');
        }
        return this._isIpad;
    }

    private _isIphone: boolean = undefined;
    get isIphone(): boolean {
        if (this._isIphone === undefined) {
            this._isIphone = this.platform.is('iphone');
        }
        return this._isIphone;
    }

    private _isMobile: boolean = undefined;
    get isMobile(): boolean {
        if (this._isMobile === undefined) {
            // If we're on desktop, we're definitely not mobile
            if (this.isDesktop) {
                this._isMobile = false;
            } else {
                this._isMobile = this.platform.is('mobile') || this.platform.is('iphone') || this.platform.is('android');
                this._isMobile = this._isMobile && !this.isTablet;
            }
        }
        return this._isMobile;
    }

    private _isMobileWeb: boolean = undefined;
    get isMobileWeb(): boolean {
        if (this._isMobileWeb === undefined) {
            // If we're on desktop, we're definitely not mobile web
            if (this.isDesktop) {
                this._isMobileWeb = false;
            } else {
                this._isMobileWeb = this.platform.is('mobileweb');
            }
        }
        return this._isMobileWeb;
    }

    private _isTablet: boolean = undefined;
    get isTablet(): boolean {
        if (this._isTablet === undefined) {
            this._isTablet = this.platform.is('tablet') || this.platform.is('ipad');
        }
        return this._isTablet;
    }

    private _isWeb: boolean = undefined;
    get isWeb(): boolean {
        if (this._isWeb === undefined) {
            // Desktop is always web
            if (this.isDesktop) {
                this._isWeb = true;
            } else {
                this._isWeb = this.platform.is('mobileweb') || this.platform.is('pwa') || this.platform.is('desktop');
            }
        }
        return this._isWeb;
    }

    // endregion

    // region Initialization

    constructor(
        private readonly platform: Platform,
    ) {
        this.logPlatformReport();
    }

    // endregion

    // region Methods

    getDevicePlatform(): DevicePlatform {
        if (this.isIos) {
            return DevicePlatform.Ios;
        }
        if (this.isAndroid) {
            return DevicePlatform.Android;
        }
        if (this.isWeb) {
            return DevicePlatform.Web;
        }
        return null;
    }

    getPlatformColCount(): number {
        return this.isMobile || this.isMobileWeb ? 1 : 2;
    }

    getPlatformDesignCode(): string {
        if (this.isIos) {
            return 'ios';
        }
        if (this.isAndroid) {
            return 'md';
        }
        return 'core';
    }

    logPlatformReport(): void {
        let isAndroid = this.isAndroid;
        let isDesktop = this.isDesktop;
        let isIos = this.isIos;
        let isIpad = this.isIpad;
        let isIphone = this.isIphone;
        let isMobile = this.isMobile;
        let isMobileWeb = this.isMobileWeb;
        let isTablet = this.isTablet;
        let isWeb = this.isWeb;
        let availablePlatforms = this.platform.platforms();

        console.log(`((==)) isAndroid:   ${isAndroid}`);
        console.log(`((==)) isDesktop:   ${isDesktop}`);
        console.log(`((==)) isIos:       ${isIos}`);
        console.log(`((==)) isIpad:      ${isIpad}`);
        console.log(`((==)) isIphone:    ${isIphone}`);
        console.log(`((==)) isMobile:    ${isMobile}`);
        console.log(`((==)) isMobileWeb: ${isMobileWeb}`);
        console.log(`((==)) isTablet:    ${isTablet}`);
        console.log(`((==)) isWeb:       ${isWeb}`);
        console.log(`((==)) availablePlatforms: ${availablePlatforms}`);
    }

    // endregion
}
